import { useEffect, useRef, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const FIELD_TYPES = [
  { type: 'signature', role: 'taxpayer', label: 'Taxpayer Signature', icon: '✍️', color: '#2563EB', bg: 'rgba(37,99,235,0.13)' },
  { type: 'date',      role: 'taxpayer', label: 'Taxpayer Date',      icon: '📅', color: '#2563EB', bg: 'rgba(37,99,235,0.08)' },
  { type: 'signature', role: 'spouse',   label: 'Spouse Signature',   icon: '✍️', color: '#059669', bg: 'rgba(5,150,105,0.13)' },
  { type: 'date',      role: 'spouse',   label: 'Spouse Date',        icon: '📅', color: '#059669', bg: 'rgba(5,150,105,0.08)' },
]
const DEFAULT_SIZES = { signature: [0.22, 0.035], date: [0.14, 0.030], initials: [0.06, 0.035], text: [0.20, 0.030] }
const ROLE_COLOR = { taxpayer: '#2563EB', spouse: '#059669', client: '#CBB17B' }

// How many pages to pre-render ahead of the visible area
const PRELOAD_AHEAD = 2

async function authedFetch(path, body) {
  const { data: { session } } = await supabase.auth.getSession()
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
    body: JSON.stringify(body)
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.error || `Request failed (${res.status})`)
  return json
}

export default function DocumentEditorPage() {
  const { documentId } = useParams()
  const { profile } = useAuth()
  return <DocumentEditor key={`${documentId}:${profile?.id || ''}`} />
}

function DocumentEditor() {
  const { documentId } = useParams()
  const navigate = useNavigate()
  const { profile } = useAuth()

  const [doc, setDoc] = useState(null)
  const [pages, setPages] = useState({})          // pageNum -> dataUrl | 'loading' | 'error'
  const [imageReady, setImageReady] = useState({}) // only true after the browser decodes a page image
  const [pageCount, setPageCount] = useState(0)
  const [fields, setFields] = useState([])
  const [activeType, setActiveType] = useState(null)
  const [dragging, setDragging] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState('')
  const [error, setError] = useState('')
  const pageRefs = useRef({})                     // pageNum -> DOM element ref
  const loadingPages = useRef(new Set())          // in-flight requests
  const loadedPages = useRef(new Set())           // completed pages (ref, not state — observer closures are stale)          // prevent duplicate requests

  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000) }

  useEffect(() => { if (documentId && profile?.id) loadDoc() }, [documentId])

  async function loadDoc() {
    setLoading(true); setError('')
    try {
      const { data: d } = await supabase
        .from('documents')
        .select('*, clients(full_name, business_name, cpa_id)')
        .eq('id', documentId).maybeSingle()
      if (!d) throw new Error('Document not found')
      if (d.clients?.cpa_id !== profile?.id) throw new Error('Not authorized')
      if (['partial', 'complete'].includes(d.signing_status)) throw new Error('Signing has already started. Upload a new document to change the signature fields.')
      setDoc(d)

      const { data: f, error: fieldsError } = await supabase
        .from('document_signature_fields')
        .select('*').eq('document_id', documentId).eq('confirmed', true)
      if (fieldsError) throw new Error(fieldsError.message)
      setFields(f || [])

      // Load page 1 to get page count, then show UI
      await loadPage(documentId, 1)
      setLoading(false)
    } catch (err) {
      setError(err.message); setLoading(false)
    }
  }

  async function loadPage(docId, p) {
    if (loadedPages.current.has(p) || loadingPages.current.has(p)) return
    loadingPages.current.add(p)
    setPages(prev => prev[p] && prev[p] !== 'error' ? prev : ({ ...prev, [p]: 'loading' }))
    try {
      const render = await authedFetch('/.netlify/functions/render-document-page', { document_id: docId || documentId, page: p })
      if (!render.image || (p === 1 && !render.page_count)) throw new Error('Document preview unavailable')
      if (p === 1) setPageCount(render.page_count)
      loadedPages.current.add(p)
      setPages(prev => ({ ...prev, [p]: render.image }))
    } catch {
      setPages(prev => ({ ...prev, [p]: 'error' }))
    } finally {
      loadingPages.current.delete(p)
    }
  }

  // Intersection observer: load a page when its placeholder scrolls into view
  useEffect(() => {
    if (!pageCount || !documentId) return
    const obs = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        const p = parseInt(entry.target.dataset.page)
        if (!p) continue
        loadPage(documentId, p)
        // Pre-load ahead
        for (let i = 1; i <= PRELOAD_AHEAD; i++) {
          if (p + i <= pageCount) loadPage(documentId, p + i)
        }
      }
    }, { rootMargin: '200px' })

    // Observe all page placeholders
    Object.values(pageRefs.current).forEach(el => { if (el) obs.observe(el) })
    return () => obs.disconnect()
  }, [pageCount, documentId])

  // ---- Click to place ----
  function onPageClick(e, pageNum) {
    if (!activeType || dragging || saving || !imageReady[pageNum]) return
    const rect = e.currentTarget.getBoundingClientRect()
    const [wf, hf] = DEFAULT_SIZES[activeType.type] || [0.18, 0.035]
    const xFrac = clampPosition((e.clientX - rect.left) / rect.width - wf / 2, wf)
    const yFrac = clampPosition((e.clientY - rect.top) / rect.height - hf / 2, hf)
    setFields(prev => [...prev, {
      id: crypto.randomUUID(), signer_role: activeType.role, field_type: activeType.type,
      page_number: pageNum,
      x_frac: round4(xFrac), y_frac: round4(yFrac),
      w_frac: wf, h_frac: hf,
      detected: false, confirmed: true
    }])
    setActiveType(null)
  }

  // ---- Drag to reposition ----
  function onBoxMouseDown(e, index) {
    if (activeType || saving || !imageReady[fields[index].page_number || 1]) return
    e.stopPropagation(); e.preventDefault()
    const pageEl = pageRefs.current[fields[index].page_number]
    const rect = pageEl?.getBoundingClientRect() || { width: 1, height: 1 }
    setDragging({ index, startX: e.clientX, startY: e.clientY, origX: fields[index].x_frac, origY: fields[index].y_frac, rectW: rect.width, rectH: rect.height })
  }

  const onMouseMove = useCallback((e) => {
    if (!dragging || saving) return
    const dx = (e.clientX - dragging.startX) / dragging.rectW
    const dy = (e.clientY - dragging.startY) / dragging.rectH
    setFields(prev => prev.map((f, i) => i !== dragging.index ? f : {
      ...f, x_frac: round4(clampPosition(dragging.origX + dx, f.w_frac)), y_frac: round4(clampPosition(dragging.origY + dy, f.h_frac))
    }))
  }, [dragging, saving])

  const onMouseUp = useCallback(() => setDragging(null), [])

  useEffect(() => {
    if (dragging) { window.addEventListener('mousemove', onMouseMove); window.addEventListener('mouseup', onMouseUp) }
    return () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp) }
  }, [dragging, onMouseMove, onMouseUp])

  function removeField(index) {
    if (saving) return
    setFields(prev => prev.filter((_, i) => i !== index))
  }

  async function saveAndSend() {
    if (saving) return
    const roles = new Set(fields.filter(f => f.field_type === 'signature').map(f => f.signer_role))
    if (!roles.size) { notify('Place at least one signature field first'); return }
    if (fields.some(f => !['signature', 'date'].includes(f.field_type))) { notify('Remove unsupported fields. Only signatures and dates can be sent.'); return }
    if (fields.some(f => f.field_type === 'date' && !roles.has(f.signer_role))) { notify('Add a signature for each person with a date field.'); return }
    if (!pageCount || fields.some(f => !imageReady[f.page_number || 1])) { notify('Wait for each page with fields to load.'); return }
    setSaving(true); setError('')
    try {
      const { data: signed, error: signedError } = await supabase.from('document_signatures').select('id').eq('document_id', documentId).limit(1)
      if (signedError) throw new Error(signedError.message)
      if (signed?.length) throw new Error('Signing has already started. Upload a new document to change the fields.')
      // Save before removing old fields, so a failed write cannot erase the layout.
      const rows = fields.map(f => ({ id: f.id || crypto.randomUUID(), document_id: documentId, signer_role: f.signer_role, field_type: f.field_type, page_number: f.page_number || 1, x_frac: f.x_frac, y_frac: f.y_frac, w_frac: f.w_frac, h_frac: f.h_frac, detected: !!f.detected, confirmed: true, label: f.label || null }))
      setFields(rows)
      const { error: saveError } = await supabase.from('document_signature_fields').upsert(rows)
      if (saveError) throw new Error(saveError.message)
      const { error: removeError } = await supabase.from('document_signature_fields').delete().eq('document_id', documentId).not('id', 'in', `(${rows.map(f => f.id).join(',')})`)
      if (removeError) throw new Error(removeError.message)
      const { error: statusError } = await supabase.from('documents').update({ signing_status: 'pending', requires_signature: true }).eq('id', documentId)
      if (statusError) throw new Error(statusError.message)
      try {
        const notification = await authedFetch('/.netlify/functions/notify-client', { type: 'document_sent', client_id: doc.client_id, data: { file_names: [doc.file_name], requires_signature: true } })
        if (notification.skipped) {
          setError('Fields are saved in the client portal. No notification was sent because the client has no email address on file.')
          return
        }
      } catch (err) {
        setError('Fields are saved and available in the client portal, but the notification failed. You can retry Confirm & Notify Client.')
        return
      }
      notify('Fields saved — client notified!')
      setTimeout(() => navigate(-1), 1200)
    } catch (err) { setError(err.message) }
    finally { setSaving(false) }
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh', flexDirection: 'column', gap: 16 }}>
      <div style={{ width: 40, height: 40, border: '3px solid #DED6CA', borderTopColor: '#CBB17B', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <div style={{ color: '#62615D', fontSize: 14 }}>Loading document…</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )

  if (error && (!doc || !pageCount)) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
      <div style={{ color: '#991B1B', marginBottom: 16 }}>{error}</div>
      <button className="btn btn-ghost" onClick={() => navigate(-1)}>← Back</button>
      {!doc?.signing_status || !['partial', 'complete'].includes(doc.signing_status) ? <button className="btn btn-ghost" onClick={loadDoc}>Try again</button> : null}
    </div>
  )

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 160px)', minHeight: 480, overflow: 'hidden', background: '#F7F3EB' }}>

      {/* ---- Left palette ---- */}
      <div style={{ width: 220, flexShrink: 0, background: '#202326', display: 'flex', flexDirection: 'column', padding: '16px 12px', overflowY: 'auto' }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 12, cursor: 'pointer', textAlign: 'left', marginBottom: 16, padding: 0 }}>← Back</button>
        <div style={{ color: '#CBB17B', fontFamily: "'DM Sans',sans-serif", fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{doc?.file_name}</div>
        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginBottom: 4 }}>{doc?.clients?.business_name || doc?.clients?.full_name}</div>
        {pageCount > 0 && <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, marginBottom: 16 }}>{pageCount} page{pageCount !== 1 ? 's' : ''}</div>}

        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Field Types</div>
        {FIELD_TYPES.map((ft, i) => {
          const active = activeType?.type === ft.type && activeType?.role === ft.role
          return (
            <button key={i} disabled={saving} onClick={() => setActiveType(active ? null : { type: ft.type, role: ft.role })}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 10px', borderRadius: 8, marginBottom: 6, border: `1.5px solid ${active ? ft.color : 'transparent'}`, background: active ? ft.bg : 'rgba(255,255,255,0.05)', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
              <span style={{ fontSize: 14, width: 20, textAlign: 'center', color: ft.color }}>{ft.icon}</span>
              <span style={{ fontSize: 12, color: active ? '#fff' : 'rgba(255,255,255,0.7)', fontWeight: active ? 700 : 400 }}>{ft.label}</span>
            </button>
          )
        })}
        {activeType && (
          <div style={{ marginTop: 8, padding: '8px 10px', background: 'rgba(201,168,76,0.15)', borderRadius: 8, fontSize: 11, color: '#CBB17B', textAlign: 'center' }}>
            Click any page to place
          </div>
        )}

        <div style={{ flex: 1 }} />

        {fields.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
              {fields.length} field{fields.length !== 1 ? 's' : ''} placed
            </div>
            {fields.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px', marginBottom: 3, borderRadius: 6, background: 'rgba(255,255,255,0.05)' }}>
                <span style={{ fontSize: 11, color: ROLE_COLOR[f.signer_role] || '#fff' }}>
                  p{f.page_number} · {f.signer_role} {f.field_type}
                </span>
                <button disabled={saving} aria-label={`Remove ${f.signer_role} ${f.field_type}`} onClick={() => removeField(i)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: 14, padding: 0 }}>×</button>
              </div>
            ))}
          </div>
        )}

        <button className="btn btn-gold" onClick={saveAndSend} disabled={saving || !fields.some(f => f.field_type === 'signature')} style={{ width: '100%', padding: 11, fontSize: 13, fontWeight: 700 }}>
          {saving ? 'Saving…' : 'Confirm & Notify Client'}
        </button>
        {error && <div style={{ marginTop: 8, fontSize: 11, color: '#FCA5A5', textAlign: 'center' }}>{error}</div>}
      </div>

      {/* ---- Scrollable page stack ---- */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
        {pageCount === 0 && (
          <div style={{ color: '#62615D', fontSize: 14, marginTop: 40 }}>{pages[1] === 'error' ? <button className="btn btn-ghost" onClick={() => loadPage(documentId, 1)}>Could not load document. Try again</button> : 'Rendering document…'}</div>
        )}
        {Array.from({ length: pageCount }, (_, i) => i + 1).map(p => (
          <div key={p} style={{ width: '100%', maxWidth: 820 }}>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 6, textAlign: 'center' }}>Page {p} of {pageCount}</div>
            <div
              ref={el => { pageRefs.current[p] = el }}
              data-page={p}
              onClick={(e) => onPageClick(e, p)}
              style={{ position: 'relative', width: '100%', boxShadow: '0 4px 32px rgba(0,0,0,0.18)', borderRadius: 4, overflow: 'visible', cursor: activeType ? 'crosshair' : 'default', background: '#e8e4dc', minHeight: 200 }}>

              {pages[p] && pages[p] !== 'loading' && pages[p] !== 'error'
                ? <img src={pages[p]} alt={`Page ${p}`} onLoad={() => setImageReady(prev => ({ ...prev, [p]: true }))} onError={() => { loadedPages.current.delete(p); setImageReady(prev => ({ ...prev, [p]: false })); setPages(prev => ({ ...prev, [p]: 'error' })) }} style={{ width: '100%', display: 'block', borderRadius: 4, userSelect: 'none', pointerEvents: 'none' }} draggable={false} />
                : <div style={{ width: '100%', paddingBottom: '129%', position: 'relative' }}>
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {pages[p] === 'error'
                        ? <button className="btn btn-ghost" onClick={e => { e.stopPropagation(); loadPage(documentId, p) }}>Could not load page {p}. Try again</button>
                        : <div style={{ width: 28, height: 28, border: '3px solid #D4C9B5', borderTopColor: '#CBB17B', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />}
                    </div>
                  </div>
              }

              {/* Fields for this page */}
              {imageReady[p] && fields.filter(f => (f.page_number || 1) === p).map((f, localIdx) => {
                const globalIdx = fields.indexOf(f)
                const ft = FIELD_TYPES.find(t => t.type === f.field_type && t.role === f.signer_role) || FIELD_TYPES[0]
                const isDragging = dragging?.index === globalIdx
                return (
                  <div key={globalIdx}
                    onMouseDown={(e) => onBoxMouseDown(e, globalIdx)}
                    onDoubleClick={() => removeField(globalIdx)}
                    title="Drag to move · Double-click to remove"
                    style={{
                      position: 'absolute',
                      left: `${f.x_frac * 100}%`, top: `${f.y_frac * 100}%`,
                      width: `${f.w_frac * 100}%`, height: `${f.h_frac * 100}%`,
                      border: `2px solid ${ft.color}`,
                      background: isDragging ? ft.bg.replace(/[\d.]+\)$/, '0.28)') : ft.bg,
                      borderRadius: 3, boxSizing: 'border-box',
                      cursor: isDragging ? 'grabbing' : 'grab',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      userSelect: 'none', zIndex: isDragging ? 10 : 2,
                      boxShadow: isDragging ? `0 2px 12px ${ft.color}55` : 'none'
                    }}>
                    <span style={{ fontSize: 9, fontWeight: 700, color: ft.color, textTransform: 'uppercase', letterSpacing: 0.3, pointerEvents: 'none' }}>
                      {ft.icon} {f.signer_role} {f.field_type}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
        <div style={{ height: 40 }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, background: '#202326', color: '#fff', padding: '11px 18px', borderRadius: 10, fontSize: 13, fontWeight: 500, boxShadow: '0 8px 28px rgba(0,0,0,0.22)' }}>
          ✓ {toast}
        </div>
      )}
    </div>
  )
}

function round4(n) { return Math.round(n * 10000) / 10000 }
function clampPosition(n, size) { return Math.max(0, Math.min(1 - Number(size), n)) }
